import { Link } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import {
  formatDue,
  isSessionActive,
  listAssignments,
  listAttendanceRecords,
  listAttendanceSessions,
  listAssessments,
  listMaterials,
  listNotifications,
} from '@/lib/api'
import { useAsyncData } from '@/lib/useAsyncData'

export function StudentDashboard() {
  const { user } = useAuth()
  const sessions = useAsyncData(listAttendanceSessions, [])
  const records = useAsyncData(listAttendanceRecords, [])
  const materials = useAsyncData(listMaterials, [])
  const assignments = useAsyncData(listAssignments, [])
  const assessments = useAsyncData(listAssessments, [])
  const notifications = useAsyncData(listNotifications, [])

  const active = (sessions.data || []).find((s) => isSessionActive(s))
  const signedSessionIds = new Set((records.data || []).map((r) => r.session))
  const attendancePct =
    (sessions.data || []).length === 0
      ? '—'
      : `${Math.round(
          ((records.data || []).filter((r) => r.status === 'PRESENT').length /
            Math.max((sessions.data || []).length, 1)) *
            100,
        )}%`
  const upcomingAssessments = (assessments.data || [])
    .filter((a) => !a.date_held || new Date(a.date_held) >= new Date(new Date().toDateString()))
    .slice(0, 5)
  const pendingAssignments = (assignments.data || []).slice(0, 5)
  const recentMaterials = (materials.data || []).slice(0, 4)
  const unread = (notifications.data || []).filter((n) => !n.is_read).length
  const loading =
    sessions.loading ||
    records.loading ||
    materials.loading ||
    assignments.loading ||
    assessments.loading

  return (
    <>
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200/80 bg-white/80 px-8 py-4 backdrop-blur-md">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Student Portal</h2>
          <p className="text-xs text-slate-500">
            Welcome back,{' '}
            <span className="font-semibold text-slate-700">{user?.first_name || user?.username}</span>!
          </p>
        </div>
        <button type="button" className="relative rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <i className="fa-solid fa-bell text-lg" />
          {unread > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-blue-600" />}
        </button>
      </header>

      <div className="mx-auto max-w-7xl space-y-8 p-8">
        {loading && <p className="text-sm text-slate-500">Loading dashboard…</p>}
        {(sessions.error || materials.error || assignments.error) && (
          <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
            {sessions.error || materials.error || assignments.error}
          </p>
        )}

        {active ? (
          <div className="relative flex flex-col items-center justify-between gap-4 overflow-hidden rounded-2xl bg-blue-600 p-6 text-white shadow-xl md:flex-row">
            <div className="z-10 space-y-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/50 px-3 py-1 text-[10px] font-bold tracking-wider uppercase backdrop-blur-sm">
                Attendance session active
              </span>
              <h3 className="text-2xl font-black tracking-tight">
                {active.course_title} ({active.course_code})
              </h3>
              <p className="flex items-center gap-2 text-xs text-blue-100">
                <span>Lecturer: {active.lecturer_name || '—'}</span>
                <span>•</span>
                <span>
                  {active.start_time.slice(0, 5)} – {active.end_time.slice(0, 5)}
                </span>
                {signedSessionIds.has(active.id) && <span>• Signed</span>}
              </p>
            </div>
            <div className="z-10 flex items-center gap-3">
              <Link
                to="/student/attendance"
                className="flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-xs font-bold text-blue-700 shadow-md hover:bg-slate-50"
              >
                <i className="fa-solid fa-hand text-blue-600" /> Sign attendance
              </Link>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500">
            No active attendance session right now.
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <StatCard
            label="Overall attendance"
            value={attendancePct}
            hint={`${(records.data || []).length} signed / ${(sessions.data || []).length} sessions`}
            hintClass="text-emerald-600"
            icon="fa-user-check"
            iconWrap="bg-emerald-50 text-emerald-500"
          />
          <StatCard
            label="Upcoming tests/quizzes"
            value={String(upcomingAssessments.length)}
            hint={upcomingAssessments[0] ? upcomingAssessments[0].title : 'None scheduled'}
            hintClass="text-amber-600"
            icon="fa-pen-ruler"
            iconWrap="bg-amber-50 text-amber-500"
          />
          <StatCard
            label="Pending assignments"
            value={String(pendingAssignments.length)}
            hint={pendingAssignments[0] ? pendingAssignments[0].title : 'All clear'}
            hintClass="text-blue-600"
            icon="fa-file-pen"
            iconWrap="bg-blue-50 text-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <h3 className="text-base font-bold text-slate-800">Recent Notes & Materials</h3>
              <Link to="/student/materials" className="text-xs font-semibold text-blue-600 hover:underline">
                View All
              </Link>
            </div>
            {recentMaterials.length === 0 && <p className="text-sm text-slate-500">No materials yet.</p>}
            {recentMaterials.map((m) => (
              <MaterialRow
                key={m.id}
                title={m.title}
                course={`${m.course_title} (${m.course_code})`}
                href={m.external_link || m.file || undefined}
              />
            ))}
          </section>

          <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <h3 className="text-base font-bold text-slate-800">Upcoming Deadlines</h3>
            </div>
            {pendingAssignments.length === 0 && upcomingAssessments.length === 0 && (
              <p className="text-sm text-slate-500">No upcoming deadlines.</p>
            )}
            {pendingAssignments.map((a) => (
              <DeadlineRow key={`asg-${a.id}`} title={a.title} when={formatDue(a.due_date)} />
            ))}
            {upcomingAssessments.map((a) => (
              <DeadlineRow
                key={`asm-${a.id}`}
                title={a.title}
                when={a.date_held ? formatDue(`${a.date_held}T09:00:00`) : 'TBA'}
              />
            ))}
          </section>
        </div>
      </div>
    </>
  )
}

function StatCard({
  label,
  value,
  hint,
  hintClass,
  icon,
  iconWrap,
}: {
  label: string
  value: string
  hint: string
  hintClass: string
  icon: string
  iconWrap: string
}) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
      <div className="space-y-1">
        <p className="text-xs font-bold tracking-wider text-slate-400 uppercase">{label}</p>
        <h3 className="text-3xl font-black text-slate-800">{value}</h3>
        <span className={`mt-1 inline-block text-[11px] font-semibold ${hintClass}`}>{hint}</span>
      </div>
      <div className={`rounded-2xl p-3 ${iconWrap}`}>
        <i className={`fa-solid ${icon} text-2xl`} />
      </div>
    </div>
  )
}

function MaterialRow({ title, course, href }: { title: string; course: string; href?: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3.5 transition hover:bg-slate-100/80">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-blue-100 p-2.5 text-blue-500">
          <i className="fa-solid fa-file-lines text-xl" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="text-xs text-slate-500">{course}</p>
        </div>
      </div>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600">
          Open
        </a>
      ) : (
        <span className="text-xs text-slate-400">No link</span>
      )}
    </div>
  )
}

function DeadlineRow({ title, when }: { title: string; when: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
      <p className="text-sm font-medium text-slate-800">{title}</p>
      <span className="text-xs text-slate-500">{when}</span>
    </div>
  )
}
